// AccountDetails.jsx - OSHMS Version with Remake Support
import React, { useState, useEffect } from "react";
import PatientDetails from "./patientDetails.js";
import { FaRegHandshake } from "react-icons/fa";
import axios from "axios";
import Select from "react-select";
import { useParams, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import Header from "../../../header/header.js";
import Navvbar from "../../../header/Navvbar.js";
import "./accountDetails.css"; // CSS Import

const AccountDetails = ({ heading }) => {
  // State for account selection and data
  const [selectedAccount, setSelectedAccount] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState(null);
  const [initialCaseData, setInitialCaseData] = useState(null);
  const [receivingData, setReceivingData] = useState(true);
  const [formErrors, setFormErrors] = useState({
    account: "",
    provider: "",
    lastName: "",
    procedures: "",
    delivery_date: "",
  });

  const { caseId } = useParams();
  const location = useLocation(); // Get router location for remake state

  // Check if this is a remake case
  const isRemake = location.state?.isRemake || false;
  const remakeMetadata = location.state?.remakeMetadata || null;

  // Function to retrieve userLabId from localStorage
  const getUserLabId = () => {
    try {
      const directLabId = localStorage.getItem('userLabId');
      if (directLabId) return directLabId;

      const possibleKeys = ['user', 'userData', 'auth', 'currentUser', 'labUser', 'labUserData'];

      for (const key of possibleKeys) {
        const storedData = localStorage.getItem(key);
        if (storedData) {
          try {
            const parsed = JSON.parse(storedData);
            const userLabId =
              parsed.userLabId ||
              parsed.labId ||
              parsed.id ||
              (parsed.data && parsed.data.userLabId) ||
              (parsed.data && parsed.data.labId) ||
              (parsed.user && parsed.user.userLabId) ||
              (parsed.user && parsed.user.labId);

            if (userLabId) return userLabId;

            if (typeof storedData === 'string' && storedData.match(/^000000\d{2}$/)) {
              return storedData;
            }
          } catch (e) {
            continue;
          }
        }
      }

      for (let i = 0; i < localStorage.length; i++) {
        const value = localStorage.getItem(localStorage.key(i));
        if (value && value.match(/^000000\d{2}$/)) {
          return value;
        }
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  const getUserType = () => {
    const userType = localStorage.getItem('userType');
    if (!userType) {
      const labId = getUserLabId();
      return labId ? 'lab' : 'admin';
    }
    return userType;
  };

  const handleAccountChange = (event) => {
    const value = event.target.value;
    setSelectedAccount(value);
    setFormErrors((prev) => ({ ...prev, account: "" }));
  };

  const handleDataChange = (data) => {
    if (receivingData) {
      setFormData(data);
      setFormErrors((prev) => ({
        ...prev,
        provider: data?.selectedProvider?.value ? "" : prev.provider,
        lastName: data?.last_name ? "" : prev.lastName,
        procedures:
          data?.workRequested?.procedures &&
          data?.workRequested.procedures.length > 0 &&
          data?.workRequested.procedures.some((p) => p.procedure)
            ? ""
            : prev.procedures,
      }));
    }
  };

  const validateForm = () => {
    let valid = true;
    const newErrors = {
      account: "",
      provider: "",
      lastName: "",
      procedures: "",
      delivery_date: "",
    };

    if (!selectedAccount) {
      newErrors.account = "Account is required";
      valid = false;
    }
    if (!formData?.selectedProvider?.value) {
      newErrors.provider = "Provider is required";
      valid = false;
    }
    if (!formData?.last_name) {
      newErrors.lastName = "Last name is required";
      valid = false;
    }
    if (
      !formData?.workRequested?.procedures ||
      formData.workRequested.procedures.length === 0 ||
      formData.workRequested.procedures.every((p) => !p.procedure)
    ) {
      newErrors.procedures = "At least one procedure is required";
      valid = false;
    }
    if (!formData?.workRequested?.delivery_date) {
      newErrors.delivery_date = "Delivery date is required";
      valid = false;
    }

    setFormErrors(newErrors);
    return valid;
  };

  // Fetch accounts
  useEffect(() => {
    const fetchData = async () => {
      const userLabId = getUserLabId();
      const userType = getUserType();

      setLoading(true);
      setError(null);

      try {
        let fetchedAccounts = [];

        if (userType === 'lab' && userLabId) {
          const response = await axios.get(`https://hms.backend.hiragroupcloud.com/api/account/lab-accounts/${userLabId}`);
          if (response.data.success && response.data.data) {
            fetchedAccounts = response.data.data;
          } else {
            setError("No accounts found for your lab.");
          }
        } else {
          const response = await axios.get("https://hms.backend.hiragroupcloud.com/api/account/account");
          if (response.data.success && response.data.data) {
            fetchedAccounts = response.data.data;
          } else {
            setError("No accounts found.");
          }
        }

        const normalizedAccounts = fetchedAccounts
          .filter((account) => account && (account.account_name || account.name))
          .map((account) => ({
            accountId: account.accountId || account._id || "N/A",
            account_name: account.account_name || account.name || "Unnamed Account",
            address: account.address || "",
            city: account.city || "",
            state: account.state || "",
            country: account.country || "",
            addedBy: account.addedBy || "Unknown",
            source: account.source || "HMS"
          }));

        const sortedAccounts = normalizedAccounts.sort((a, b) =>
          a.account_name.toLowerCase().localeCompare(b.account_name.toLowerCase())
        );

        setAccounts(sortedAccounts);

        // Auto-select account for remake
        if (isRemake && remakeMetadata?.previousCaseData?.account_holder) {
          const matchingAccount = sortedAccounts.find(
            acc => acc.accountId === remakeMetadata.previousCaseData.account_holder
          );
          if (matchingAccount) {
            setSelectedAccount(matchingAccount.accountId);
          }
        } else if (sortedAccounts.length === 1) {
          setSelectedAccount(sortedAccounts[0].accountId);
        }

        setLoading(false);
      } catch (error) {
        let errorMessage = "Error fetching accounts.";
        if (error.response) {
          errorMessage = `Server error: ${error.response.status}`;
        } else if (error.request) {
          errorMessage = "No response from server.";
        }

        setError(errorMessage);
        setLoading(false);
      }
    };

    fetchData();
  }, [isRemake, remakeMetadata]);

  // Prefill form for editing or remake
  useEffect(() => {
    const fetchCaseData = async () => {
      if (caseId && accounts.length > 0 && !isRemake) {
        try {
          const response = await axios.get(`https://hms.backend.hiragroupcloud.com/api/cases/cases/${caseId}`);
          const caseData = response.data;
          setInitialCaseData(caseData);

          const accountMatch = accounts.find(
            (acc) =>
              acc.accountId === caseData.account_holder ||
              acc.account_name?.toLowerCase() === caseData.account_holder?.toLowerCase()
          );
          if (accountMatch) {
            setSelectedAccount(accountMatch.accountId);
          }
        } catch (error) {
          // Silent fail on prefill
        }
      } else if (isRemake && location.state?.caseData && accounts.length > 0) {
        // Use passed case data for remake
        setInitialCaseData(location.state.caseData);

        const accountMatch = accounts.find(
          (acc) => acc.accountId === location.state.caseData.account_holder
        );
        if (accountMatch) {
          setSelectedAccount(accountMatch.accountId);
        }
      }
    };

    fetchCaseData();
  }, [caseId, accounts, isRemake, location.state]);

  // Submit the case
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setReceivingData(false);

    const userLabId = getUserLabId();
    const userType = getUserType();

    if (userType === 'lab' && !userLabId) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "User Lab ID not found. Please login again.",
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false,
        confirmButtonColor: "#004aad"
      });
      return;
    }

    const formDataToSend = new FormData();

    // Always append userLabId
    if (userLabId) formDataToSend.append("userLabId", userLabId);

    formDataToSend.append("account_holder", selectedAccount);
    formDataToSend.append("first_name", formData.first_name || "");
    formDataToSend.append("last_name", formData.last_name || "");
    formDataToSend.append("userType", userType);

    // Birth date
    const birthDate = formData.birth_date;
    if (birthDate) {
      const date = birthDate instanceof Date ? birthDate : new Date(birthDate);
      formDataToSend.append("birth_date", isNaN(date) ? "" : date.toISOString());
    }

    // Notes
    formDataToSend.append("subject", formData.notes.subject || "");
    formDataToSend.append("recipient", formData.notes.recipient || "");
    formDataToSend.append("tags", JSON.stringify(formData.notes.tags || []));
    formDataToSend.append("try_in", formData.notes.buttonClickCount?.tryIn || false);
    formDataToSend.append("emerg", formData.notes.buttonClickCount?.emerg || false);
    formDataToSend.append("next_case", formData.notes.buttonClickCount?.nextCase || false);
    formDataToSend.append("pickup", formData.notes.buttonClickCount?.pickup || false);
    formDataToSend.append("editorHtml", formData.notes.editorHtml || "");

    // Attached files
    if (formData.notes.attachedFiles?.length > 0) {
      formData.notes.attachedFiles.forEach((file) => {
        formDataToSend.append("attached_files", file);
      });
    }

    // Provider & gender
    formDataToSend.append("gender", formData.selectedGender?.value || "");
    formDataToSend.append("provider_name", formData.selectedProvider?.value || "");

    // Dates
    const appendDate = (key, dateValue) => {
      if (dateValue) {
        const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
        formDataToSend.append(key, isNaN(date) ? "" : date.toISOString());
      } else {
        formDataToSend.append(key, "");
      }
    };

    appendDate("start_date", formData.workRequested.start_date);
    appendDate("arrive_date", formData.workRequested.arrive_date);
    appendDate("delivery_date", formData.workRequested.delivery_date);

    // Color & pan number
    formDataToSend.append("color", formData.workRequested.color?.name || "");
    formDataToSend.append("pan_number", formData.workRequested.pan_number || "");
    formDataToSend.append("procedures", JSON.stringify(formData.workRequested.procedures || []));

    if (formData.workRequested.components) {
      formDataToSend.append("components", JSON.stringify(formData.workRequested.components));
    }

    // === ADD REMAKE DATA IF THIS IS A REMAKE ===
    if (isRemake && remakeMetadata) {
      formDataToSend.append("isRemake", true);
      formDataToSend.append("remakeMetadata", JSON.stringify(remakeMetadata));
    }

    try {
      const response = await axios.post(
        "https://hms.backend.hiragroupcloud.com/api/cases/case",
        formDataToSend,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const caseNumber = response.data.caseNumber;
      if (caseNumber) {
        const message = isRemake
          ? `Remake case created successfully!\nNew Case: #${caseNumber}\nOriginal Case: #${remakeMetadata.previousCaseNumber}`
          : `Case created successfully with Case ID: #${caseNumber}`;

        Swal.fire({
          icon: "success",
          title: "Success!",
          text: message,
          timer: 2500,
          timerProgressBar: true,
          showConfirmButton: false,
          confirmButtonColor: "#004aad"
        }).then(() => {
          window.location.href = `/case/${caseNumber}`;
        });

        setTimeout(() => {
          window.location.href = `/case/${caseNumber}`;
        }, 2500);
      }
    } catch (error) {
      console.error("Error submitting case:", error);
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: error.response?.data?.message || "There was an error creating the case. Please try again.",
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false,
        confirmButtonColor: "#004aad"
      });
      setReceivingData(true);
    }
  };

  return (
    <>
      <Header />
      <Navvbar />
      <div className="account-details-container">
        <h2 className="fancy-heading">
          {isRemake && remakeMetadata
            ? `Remake Case from #${remakeMetadata.previousCaseNumber}`
            : heading}
        </h2>

        {/* Remake info banner */}
        {isRemake && remakeMetadata && (
          <div className="remake-banner">
            <div className="remake-info">
              <strong className="remake-title">🔄 Creating Remake Case</strong>
              <p className="remake-details">
                Original Case: <strong>#{remakeMetadata.previousCaseNumber}</strong> |
                Reason: <strong>{remakeMetadata.reason}</strong>
              </p>
              {remakeMetadata.description && (
                <p className="remake-description">
                  {remakeMetadata.description}
                </p>
              )}
            </div>
            <div className="remake-tags">
              {remakeMetadata.fullArchTemp && (
                <span className="remake-tag remake-tag-full-arch">
                  Full Arch Temp
                </span>
              )}
              {remakeMetadata.uber && (
                <span className="remake-tag remake-tag-uber">
                  Uber
                </span>
              )}
            </div>
          </div>
        )}

        <h3 className="title">
          <FaRegHandshake />
          Account Details
        </h3>

        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        )}

        <div className="account-section">
          <div className="row">
            <div className="account-field">
              <label htmlFor="account" className="form-label">
                Account <span className="required-star">*</span>
              </label>

              <Select
                id="account"
                name="account"
                options={accounts.map((account) => ({
                  value: account.accountId,
                  label: account.account_name,
                }))}
                value={
                  selectedAccount
                    ? {
                        value: selectedAccount,
                        label: accounts.find((a) => a.accountId === selectedAccount)?.account_name || "",
                      }
                    : null
                }
                onChange={(selected) => {
                  setSelectedAccount(selected?.value || "");
                  setFormErrors((prev) => ({ ...prev, account: "" }));
                }}
                isSearchable
                placeholder={loading ? "Loading accounts..." : "Select Account"}
                classNamePrefix="react-select"
                isLoading={loading}
                isDisabled={loading}
                noOptionsMessage={() => "No accounts available"}
              />

              {formErrors.account && (
                <div style={{
                  color: "red",
                  fontSize: "clamp(7px, 0.4vw, 12px)",
                  marginTop: "2px"
                }}>
                  {formErrors.account}
                </div>
              )}

              {!loading && (
                <div className="account-count">
                  {accounts.length} account(s) available
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Patient Details Form */}
        {selectedAccount && (
          <PatientDetails
            account={selectedAccount}
            onDataChange={handleDataChange}
            formErrors={formErrors}
            initialCaseData={initialCaseData}
          />
        )}

        {/* Submit Button - IMPROVED VERSION */}
        <div className="submit-button-container">
          <button
            onClick={handleSubmit}
            className={`submit-button ${isRemake ? 'submit-button-remake' : ''}`}
            disabled={!selectedAccount}
          >
            {isRemake ? "🔄 Create Remake Case" : "Submit Case"}
          </button>
        </div>
      </div>
    </>
  );
};

export default AccountDetails;